import { Link } from "react-router-dom";
import { ArrowRight, Target, Compass, Award, ShieldCheck } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { IMAGES } from "../data/content";

const pillars = [
  { icon: Target, title: "Hard Work", text: "We show up early, work with purpose, and never cut corners on your property." },
  { icon: Award, title: "Craftsmanship", text: "Clean edges, healthy turf, and hardscaping built to stand the test of time." },
  { icon: ShieldCheck, title: "Responsibility", text: "We do what we say, when we say — and we stand behind every job." },
  { icon: Compass, title: "Legacy", text: "Building something lasting for our family, our crew, and our community." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="bg-charcoal text-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal><Eyebrow>Our Story</Eyebrow></Reveal>
          <Reveal delay={1}>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-700 mt-5 max-w-3xl leading-[1.08] tracking-tight text-balance">
              A growing local company, built on strong values.
            </h1>
          </Reveal>
          <Reveal delay={2}>
            <p className="text-white/75 text-lg mt-6 max-w-2xl leading-relaxed">
              Legacy Lawn &amp; Landscape isn't a faceless service. We're a veteran-owned, family-built
              company that believes your property deserves the same care we'd give our own.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="relative">
              <img src={IMAGES.founders} alt="Father and son landscaping team" className="w-full h-[520px] object-cover rounded-sm" style={{ objectPosition: "50% 22%" }} />
              <div className="absolute -bottom-6 -right-6 hidden md:block bg-emerald text-white p-6 rounded-sm max-w-[220px]">
                <p className="font-display text-2xl font-600 leading-snug">Father &amp; son. One standard.</p>
              </div>
            </div>
          </Reveal>
          <div>
            <Reveal><Eyebrow>How We Started</Eyebrow></Reveal>
            <Reveal delay={1}>
              <h2 className="font-display text-3xl sm:text-4xl font-600 text-charcoal mt-4 mb-6 leading-tight">
                From a shared truck bed to a trusted local name.
              </h2>
            </Reveal>
            <Reveal delay={2}>
              <div className="space-y-5 text-olive text-lg leading-relaxed">
                <p>
                  It started the way the best things do — a father and son, a few tools, and a refusal
                  to do anything halfway. Adrian brought the discipline and grit of a veteran. David brought
                  the drive to build something that would outlast them both.
                </p>
                <p>
                  Today, Legacy Lawn &amp; Landscape serves Rincon, Springfield, Guyton, Pooler, and the
                  surrounding communities with full-service lawn care, planting, hardscaping, and drainage —
                  all held to professional standards with a family's personal touch.
                </p>
                <p className="text-charcoal font-500">
                  We're not just maintaining lawns. We're building a reputation, a business, and a legacy.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-16">
            <Reveal><Eyebrow>What We Stand For</Eyebrow></Reveal>
            <Reveal delay={1}>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-600 text-charcoal mt-4 tracking-tight">
                The values behind every job.
              </h2>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {pillars.map((p, i) => (
              <Reveal key={p.title} delay={i}>
                <div className="h-full bg-white p-8 border border-border rounded-sm hover:-translate-y-1 hover:shadow-md transition-all duration-300" data-testid={`pillar-${i}`}>
                  <span className="grid place-items-center w-12 h-12 rounded-sm bg-emerald/10 text-emerald mb-5">
                    <p.icon className="w-6 h-6" strokeWidth={1.75} />
                  </span>
                  <h3 className="font-display text-xl font-600 text-charcoal mb-2">{p.title}</h3>
                  <p className="text-olive text-[15px] leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-emerald text-white py-20 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl font-600 leading-tight text-balance">
              Growing More Than Lawns. Building a Legacy.
            </h2>
          </Reveal>
          <Reveal delay={1}>
            <Link to="/founders" className="inline-flex items-center gap-2 bg-gold text-charcoal px-8 py-4 rounded-sm font-700 mt-8 hover:bg-gold-dark transition-colors" data-testid="about-founders-btn">
              Meet the Founders <ArrowRight className="w-5 h-5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
